import { ICellRendererParams } from 'ag-grid-community';
import { EditLined } from '@tag/tag-icons';
import { useState, useEffect, useMemo } from 'react';

import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';

import EditBundleModal from '../components/EditBundleModal/EditBundleModal';
import { NumberUtils } from '@/utils/numbers.utils';
import {
  ContractLineBillingCycleOutput,
  ContractStatusEnum,
  VariableContractLineOutput
} from '@/graphql/contracts/_generated_/contracts';
import { lineRefreshObservable } from '../components/ContractLinesGrid/linesSelectionObservable';
import { variableLineRefreshObservable } from '../components/ContractLinesGrid/variableLinesSelectionObservable';


export default function BundleGroupingCellRenderer(params: ICellRendererParams) {
  const [isOpenEditBundleModal, setIsOpenEditBundleModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };
    getRole();
  }, [getUserRole]);

  const isUserRole = userRole === UserRole.USER;
  const isReaderRole = userRole === UserRole.READER;
  const isRestrictedRole = isUserRole || isReaderRole;

  const firstGroupedRowData = params.value ? params.node.allLeafChildren?.[0]?.data : null;
  const allLines = useMemo(
    () => params.node.allLeafChildren?.map(child => child.data) || [],
    [params.node.allLeafChildren, refreshTrigger]
  );

  useEffect(() => {
    const lineSubscription = lineRefreshObservable.subscribe(() => {
      setRefreshTrigger(prev => prev + 1);
    });
    const variableSubscription = variableLineRefreshObservable.subscribe(() => {
      setRefreshTrigger(prev => prev + 1);
    });
    return () => {
      lineSubscription.unsubscribe();
      variableSubscription.unsubscribe();
    };
  }, []);

  const calculateSubtotals = useMemo(() => {
    const bcLines = (allLines.filter((line: any) =>
      'id' in line && 'annualBillingPeriodValue' in line
    ) as ContractLineBillingCycleOutput[])
      .filter(line => line.status == ContractStatusEnum.Active || line.status == ContractStatusEnum.ActiveDNR);

    const variableLines = (allLines.filter((line: any) =>
      'iD' in line && 'predictedAnnualValue' in line
    ) as VariableContractLineOutput[])
      .filter(line => line.status == ContractStatusEnum.Active || line.status == ContractStatusEnum.ActiveDNR);

    const bcSum = bcLines.reduce((sum, line) =>
      sum + parseFloat(line.annualBillingPeriodValue || '0'), 0
    );

    const variableSum = variableLines.reduce((sum, line) =>
      sum + parseFloat(line.predictedAnnualValue || '0'), 0
    );

    let subtotalText = '';
    if (bcLines.length > 0 && bcSum > 0) {
      subtotalText += `Annual Billing Value: ${
        params.data?.currency ? params.data.currency + ' ' : ''
      }${NumberUtils.prettifyWithRound(bcSum)}`;

    }
    if (variableLines.length > 0 && variableSum > 0) {
      if (subtotalText) subtotalText += ', ';
      subtotalText += `Predicted Annual Value: ${
        params.data?.currency ? params.data.currency + ' ' : ''
      }${NumberUtils.prettifyWithRound(variableSum)}`;
    }

    return subtotalText;
  }, [allLines, refreshTrigger, params.data?.currency]);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {params?.data?.bundleId && !isRestrictedRole &&
        <EditLined
          style={{ margin: '0 6px 0 0', cursor: 'pointer' }}
          onClick={() => setIsOpenEditBundleModal(true)}>
        </EditLined>
      }
      <div>
        {params?.value || 'Unbundled Contract Lines'} ({allLines.length})
        {calculateSubtotals && (
          <span style={{ marginLeft: '10px', fontWeight: 'normal', color: '#666' }}>
            [ {calculateSubtotals} ]
          </span>
        )}
      </div>

      {isOpenEditBundleModal && firstGroupedRowData && (
        <EditBundleModal
          onClose={() => setIsOpenEditBundleModal(false)}
          bundle={{
            bundleId: firstGroupedRowData?.bundleId,
            description: firstGroupedRowData?.bundleDescription,
            hideInvoiceLines: firstGroupedRowData?.bundleHideInvoiceLines,
            hideInvoiceValues: firstGroupedRowData?.bundleHideInvoiceValues,
            softwareCompany: firstGroupedRowData?.bundleSoftwareCompany,
            type: firstGroupedRowData?.bundleType
          }}
        />
      )}
    </div>
  );
}
